/**
 * Post-pair status mirror. Reads status:{shop} and renders counts + actions.
 *
 * Action buttons:
 *  - Sync now        -> POST /commands/:shop with type sync-now
 *  - Open ERPClaw    -> follow status.erpclaw_local_url, fallback docs
 *  - Disconnect      -> POST /commands/:shop with type disconnect
 *
 * Persistent feedback (see lib/activityLog.ts):
 *  - "Last sync requested at HH:MM" indicator survives page refreshes via
 *    localStorage and clears once status.last_sync_at advances past it.
 *  - Recent activity panel (collapsed) shows the last 5 merchant actions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  ResourceItem,
  ResourceList,
  Text,
} from "@shopify/polaris";
import type { StatusBlob } from "@erpclaw-shopify/shared";
import { ApiError, getStatus, postCommand } from "../lib/api";
import {
  type ActivityEntry,
  type DeliveryTier,
  type LastSyncRequest,
  appendActivity,
  clearLastSyncRequest,
  formatTimeHHMM,
  markDispatchedBefore,
  readActivity,
  readLastSyncRequest,
  writeLastSyncRequest,
} from "../lib/activityLog";

type Props = {
  shop: string;
  initial: StatusBlob;
  initialLastUpdatedAt: string;
};

const MODE_LABEL: Record<StatusBlob["status_mode"], { tone: "success" | "attention"; label: string }> = {
  active: { tone: "success", label: "Live" },
  scheduled: { tone: "success", label: "Scheduled (15 min)" },
  "on-demand": { tone: "attention", label: "On-demand" },
};

const TIER_FLASH: Record<DeliveryTier, string> = {
  active: "Sync requested. ERPClaw should respond within 10 seconds.",
  scheduled: "Sync requested. ERPClaw will pick it up at the next 15-minute push.",
  "on-demand": "Sync requested. ERPClaw will run it the next time it pushes status.",
};

const TIER_HINT: Record<DeliveryTier, string> = {
  active: "within 10 seconds",
  scheduled: "within 15 minutes",
  "on-demand": "next time ERPClaw pushes status",
};

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return "1 hour ago";
  if (hrs < 24) return `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

function activityLabel(type: ActivityEntry["type"]): string {
  return type === "sync-now" ? "Sync now" : "Disconnect";
}

function activityStatus(entry: ActivityEntry): string {
  if (entry.dispatchedAt) {
    return `Picked up by ERPClaw at ${formatTimeHHMM(entry.dispatchedAt)}`;
  }
  if (entry.dispatchTier) {
    return `Queued, ${TIER_HINT[entry.dispatchTier]}`;
  }
  return "Queued";
}

export function Dashboard({ shop, initial, initialLastUpdatedAt }: Props) {
  const [blob, setBlob] = useState<StatusBlob>(initial);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>(initialLastUpdatedAt);
  const [pending, setPending] = useState<"sync-now" | "disconnect" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncFlash, setSyncFlash] = useState<string | null>(null);
  const [lastSyncRequest, setLastSyncRequest] = useState<LastSyncRequest | null>(
    () => readLastSyncRequest(shop),
  );
  const [activity, setActivity] = useState<ActivityEntry[]>(() => readActivity(shop));

  // Reconcile persisted "Sync requested at..." with the live status blob.
  // If the server's last_sync_at has advanced past our recorded request, the
  // sync we asked for has been served. Drop the pending-request banner and
  // mark the matching activity entry as dispatched.
  useEffect(() => {
    if (!lastSyncRequest) return;
    const reqMs = new Date(lastSyncRequest.requestedAt).getTime();
    const lastMs = new Date(blob.last_sync_at).getTime();
    if (lastMs > reqMs) {
      clearLastSyncRequest(shop);
      setLastSyncRequest(null);
      const updated = markDispatchedBefore(shop, blob.last_sync_at, "sync-now");
      setActivity(updated);
    }
  }, [shop, blob.last_sync_at, lastSyncRequest]);

  // Soft-refresh every 30s so counts don't go stale while the window is open.
  useEffect(() => {
    let cancelled = false;
    const id = window.setInterval(async () => {
      try {
        const resp = await getStatus(shop);
        if (cancelled || !resp.paired) return;
        setBlob(resp.status);
        setLastUpdatedAt(resp.last_updated_at);
      } catch {
        // swallow; next tick retries
      }
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [shop]);

  const trigger = useCallback(
    async (type: "sync-now" | "disconnect") => {
      setPending(type);
      setError(null);
      setNote(null);
      const requestedAt = new Date().toISOString();
      try {
        const resp = await postCommand(shop, type);
        if (type === "sync-now") {
          setSyncFlash(TIER_FLASH[resp.delivery_tier]);
          const lsr: LastSyncRequest = { requestedAt, deliveryTier: resp.delivery_tier };
          writeLastSyncRequest(shop, lsr);
          setLastSyncRequest(lsr);
          const updated = appendActivity(shop, {
            type,
            requestedAt,
            dispatchTier: resp.delivery_tier,
          });
          setActivity(updated);
        } else {
          setNote("Disconnect queued. Refresh in a minute to confirm.");
          const updated = appendActivity(shop, {
            type,
            requestedAt,
            dispatchTier: resp.delivery_tier,
          });
          setActivity(updated);
        }
      } catch (err) {
        if (err instanceof ApiError) setError(`${err.code}: ${err.message}`);
        else setError(err instanceof Error ? err.message : "unknown error");
      } finally {
        setPending(null);
      }
    },
    [shop],
  );

  const openErpclaw = useCallback(() => {
    const url = blob.erpclaw_local_url || "https://erpclaw.ai/docs/shopify";
    window.open(url, "_blank", "noopener,noreferrer");
  }, [blob.erpclaw_local_url]);

  const staleMins = Math.floor((Date.now() - new Date(blob.last_sync_at).getTime()) / 60_000);
  const isStale = staleMins >= 30;
  const mode = MODE_LABEL[blob.status_mode];
  const hasErrors = blob.recent_errors > 0;

  // The "Last sync requested at HH:MM" indicator. While a request is pending,
  // show the waiting copy. Once last_sync_at has caught up (lastSyncRequest
  // is null), show a brief completed summary using current 24h counts.
  const syncIndicator = useMemo<string | null>(() => {
    if (lastSyncRequest) {
      const t = formatTimeHHMM(lastSyncRequest.requestedAt);
      return `Last sync requested at ${t} - waiting for ERPClaw to pick it up (${TIER_HINT[lastSyncRequest.deliveryTier]}).`;
    }
    // After a recent completion, surface the latest server-side timestamp.
    // Only show the completed line if a sync has happened in the last hour,
    // otherwise the connection card already says "Last sync N hours ago".
    const lastMs = new Date(blob.last_sync_at).getTime();
    const ageMins = Math.floor((Date.now() - lastMs) / 60_000);
    if (ageMins >= 0 && ageMins < 60 && (activity.some((e) => e.type === "sync-now"))) {
      const t = formatTimeHHMM(blob.last_sync_at);
      return `Last requested sync completed at ${t} - synced ${blob.orders_synced_last_24h} orders, posted ${blob.gl_entries_posted_last_24h} GL entries.`;
    }
    return null;
  }, [lastSyncRequest, blob.last_sync_at, blob.orders_synced_last_24h, blob.gl_entries_posted_last_24h, activity]);

  return (
    <Page
      title="ERPClaw Accounting & ERP"
      subtitle={shop}
      titleMetadata={<Badge tone={mode.tone}>{`Connected — ${mode.label}`}</Badge>}
    >
      <Layout>
        {isStale && (
          <Layout.Section>
            <Banner tone="warning" title="Sync is stale">
              <p>
                Last sync was {staleMins} minutes ago. Check that ERPClaw is running on your machine.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)} title="Something went wrong">
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        {syncFlash && (
          <Layout.Section>
            <Banner tone="info" onDismiss={() => setSyncFlash(null)}>
              <p>{syncFlash}</p>
            </Banner>
          </Layout.Section>
        )}

        {note && (
          <Layout.Section>
            <Banner tone="info" onDismiss={() => setNote(null)}>
              <p>{note}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Connection
                </Text>
                <InlineStack gap="400" wrap>
                  <Box>
                    <Text as="span" variant="bodySm" tone="subdued">
                      ERPClaw version
                    </Text>
                    <Text as="p" variant="bodyMd">
                      v{blob.erpclaw_version}
                    </Text>
                  </Box>
                  <Box>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Last sync
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {formatRelative(blob.last_sync_at)}
                    </Text>
                  </Box>
                  <Box>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Sync mode
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {mode.label}
                    </Text>
                  </Box>
                </InlineStack>
                <Text as="span" variant="bodySm" tone="subdued">
                  Status card refreshed {formatRelative(lastUpdatedAt)}
                </Text>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Activity (last 24 hours)
                </Text>
                <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                  <Card roundedAbove="sm" background="bg-surface-secondary">
                    <Box padding="300">
                      <Text as="span" variant="bodySm" tone="subdued">
                        Orders synced
                      </Text>
                      <Text as="p" variant="headingLg">
                        {blob.orders_synced_last_24h.toLocaleString()}
                      </Text>
                    </Box>
                  </Card>
                  <Card roundedAbove="sm" background="bg-surface-secondary">
                    <Box padding="300">
                      <Text as="span" variant="bodySm" tone="subdued">
                        GL entries posted
                      </Text>
                      <Text as="p" variant="headingLg">
                        {blob.gl_entries_posted_last_24h.toLocaleString()}
                      </Text>
                    </Box>
                  </Card>
                  <Card roundedAbove="sm" background={hasErrors ? "bg-surface-critical" : "bg-surface-secondary"}>
                    <Box padding="300">
                      <Text as="span" variant="bodySm" tone="subdued">
                        Errors
                      </Text>
                      <Text as="p" variant="headingLg">
                        {blob.recent_errors.toLocaleString()}
                      </Text>
                    </Box>
                  </Card>
                </InlineGrid>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Actions
                </Text>
                <ButtonGroup>
                  <Button onClick={openErpclaw} variant="primary">
                    Open ERPClaw
                  </Button>
                  <Button onClick={() => trigger("sync-now")} loading={pending === "sync-now"}>
                    Sync now
                  </Button>
                  <Button
                    onClick={() => trigger("disconnect")}
                    loading={pending === "disconnect"}
                    tone="critical"
                  >
                    Disconnect
                  </Button>
                </ButtonGroup>
                {syncIndicator && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    <i>{syncIndicator}</i>
                  </Text>
                )}
                <Text as="p" variant="bodySm" tone="subdued">
                  {blob.erpclaw_local_url
                    ? `"Open ERPClaw" launches ${blob.erpclaw_local_url}.`
                    : '"Open ERPClaw" opens the docs because ERPClaw is not reachable at a local URL.'}
                </Text>
                {activity.length > 0 && (
                  <details>
                    <summary>
                      <Text as="span" variant="bodySm">
                        Recent activity ({activity.length})
                      </Text>
                    </summary>
                    <Box paddingBlockStart="200">
                      <ResourceList
                        resourceName={{ singular: "action", plural: "actions" }}
                        items={activity}
                        renderItem={(entry) => {
                          const id = entry.requestedAt;
                          return (
                            <ResourceItem
                              id={id}
                              accessibilityLabel={`${activityLabel(entry.type)} at ${formatTimeHHMM(entry.requestedAt)}`}
                              onClick={() => {
                                /* read-only row */
                              }}
                            >
                              <BlockStack gap="050">
                                <Text as="span" variant="bodyMd" fontWeight="semibold">
                                  {activityLabel(entry.type)}
                                </Text>
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {formatTimeHHMM(entry.requestedAt)} - {activityStatus(entry)}
                                </Text>
                              </BlockStack>
                            </ResourceItem>
                          );
                        }}
                      />
                    </Box>
                  </details>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
